import { filter } from 'lodash';
import { validateMatchMaking } from 'features/forms/assignment/studentMatchMaking/helper';

export function hasTextContent(htmlContent) {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return false;
  }
  const trimmedContent = htmlContent.trim();
  if (trimmedContent.length === 0) {
    return false;
  }
  const emptyHtmlPatterns = [
    /^<p><br\s*\/?><\/p>$/i,
    /^<p><\/p>$/i,
    /^<p>\s*<\/p>$/i,
    /^<p>\s*<br\s*\/?>\s*<\/p>$/i,
    /^<div><br\s*\/?><\/div>$/i,
    /^<div><\/div>$/i,
    /^<div>\s*<\/div>$/i,
    /^<br\s*\/?>$/i,
    /^&nbsp;$/i,
    /^<p>&nbsp;<\/p>$/i,
    /^<p>\s*&nbsp;\s*<\/p>$/i,
    /^<p>\s*&nbsp;\s*<br\s*\/?>\s*<\/p>$/i,
    /^<p><br\s*\/?>\s*&nbsp;\s*<\/p>$/i,
  ];
  if (emptyHtmlPatterns.some((pattern) => pattern.test(trimmedContent))) {
    return false;
  }
  try {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlContent;
    const textContent = tempElement.textContent || tempElement.innerText || '';
    const cleanedText = textContent.replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
    return cleanedText.length > 0;
  } catch {
    return false;
  }
}

function qLabel(i) {
  return i + 1;
}

function joinQuestionNums(set) {
  return [...set].sort((a, b) => a - b).join(', ');
}

function hasEmptyChoiceOption(options) {
  return filter(options, (o) => !o).length > 0;
}

export function collectAssignmentValidationErrors({
  displayName,
  languageFeedback,
  questions,
  skipAssignmentFields = false,
  audioMultipleChoicePartialIsGroupFirst = undefined,
}) {
  const error = [];

  if (!skipAssignmentFields) {
    if (!displayName) {
      error.push({ type: 'display_name', message: 'Add a display name for this activity.' });
    }
    if (!languageFeedback) {
      error.push({ type: 'language_feedback', message: 'Choose a language for AI feedback.' });
    }
  }
  if (!Array.isArray(questions) || questions.length === 0) {
    error.push({ type: 'question_error', message: 'Add at least one question.' });
    return error;
  }

  const questionErrorNumbers = new Set();
  const instructionErrorNumbers = new Set();
  const optionErrorNumbers = new Set();
  const audioErrorNumbers = new Set();
  let hasQuestionError = false;
  let hasInstructionError = false;
  let hasOptionError = false;
  let hasAudioError = false;

  questions.forEach((q, i) => {
    const qn = qLabel(i);
    const hasQuestionContent = hasTextContent(q.question);

    if (!hasQuestionContent && q.type !== 'picture' && q.type !== 'fillingImage') {
      if (q.type === 'audioToImage') {
        instructionErrorNumbers.add(qn);
        hasInstructionError = true;
      } else if (q.type !== 'match' && q.type !== 'filling') {
        questionErrorNumbers.add(qn);
        hasQuestionError = true;
      }
    }

    if (
      q.type === 'multipleChoice' ||
      q.type === 'trueFalse' ||
      q.type === 'audioTrueFalse' ||
      q.type === 'audioMultipleChoice' ||
      q.type === 'multipleResponse'
    ) {
      if (hasEmptyChoiceOption(q.options)) {
        hasOptionError = true;
        optionErrorNumbers.add(qn);
      }
    }

    if (q.type === 'video') {
      if (!hasTextContent(q.question)) {
        questionErrorNumbers.add(qn);
        hasQuestionError = true;
      }
    }

    if (q.type === 'photoCollage') {
      if (!hasTextContent(q.question)) {
        questionErrorNumbers.add(qn);
        hasQuestionError = true;
      }
      if (q?.categoryType?.includes('Text') || q?.categoryType?.includes('Audio')) {
        if (q?.type_question_desc?.length === 0 || !q?.type_question_desc) {
          error.push({
            type: 'display_name',
            questionIndex: i,
            message: `Question ${qn}: Add a collage description.`,
          });
        }
      }
    }

    if (q.type === 'picture') {
      const hasPictureText = hasTextContent(q.question);
      if ((!q.pictures || q.pictures.length === 0 || q.pictures === '') && !hasPictureText) {
        error.push({
          type: 'display_name',
          questionIndex: i,
          message: `Question ${qn}: Add question text or at least one image.`,
        });
      }
    }

    if (q?.type === 'match') {
      error.push(...validateMatchMaking(q, i));
    }

    if (q.type === 'filling' || q.type === 'fillingImage') {
      if (q.answerSelected?.length) {
        q.answerSelected.forEach((x, qindex) => {
          if (!x || x?.length < 2) {
            if (q.response_type === 'dropdown') {
              error.push({
                type: 'filling_error',
                questionIndex: i,
                message: `Question ${qn}, blank ${qindex + 1}: Add at least two answer options for the dropdown.`,
              });
            }
          }
        });
      }

      if (q.type === 'fillingImage' || q.fillingType === 'Audio' || q.fillingType === 'Image') {
        (q.filling_image || []).forEach((element, index) => {
          if (index === 0 && (!element?.url || element?.url?.length === 0)) {
            error.push({
              type: 'display_name',
              message: `Question ${qn}, blank ${index + 1}: Add the ${q.fillingType || 'media'} for this blank.`,
            });
          }
        });
      }

      if (!hasTextContent(q.question)) {
        error.push({
          type: 'display_name',
          questionIndex: i,
          message: `Question ${qn}: Add instruction text for the sentence.`,
        });
      }

      if (q.options?.length) {
        q.options.forEach((option, qindex) => {
          if (!option || option?.trim().length === 0) {
            error.push({
              type: 'display_name',
              questionIndex: i,
              message: `Question ${qn}, blank ${qindex + 1}: Add the sentence text.`,
            });
          }
        });
      }

      if (q.answerSelected?.length) {
        q.answerSelected.forEach((x, index) => {
          if (!x || x?.length === 0 || x.every((ans) => !ans?.text || ans?.text?.length === 0)) {
            error.push({
              type: 'display_name',
              message: `Question ${qn}, blank ${index + 1}: Add at least one accepted answer.`,
            });
          }
        });
      }

      if (q.highlightWords?.length) {
        q.highlightWords.forEach((x, qindex) => {
          if (!x || x?.length === 0) {
            error.push({
              type: 'display_name',
              message: `Question ${qn}, blank ${qindex + 1}: Select at least one highlight for the answer.`,
            });
          } else if (x.some((hw) => hw?.text === '' || hw?.text?.length === 0)) {
            error.push({
              type: 'display_name',
              message: `Question ${qn}, blank ${qindex + 1}: Select at least one highlight for the answer.`,
            });
          }
        });
      }
    }

    if (q.type === 'audioToImage' || q.type === 'audioTrueFalse' || q.type === 'audioEssay') {
      if (!q.audio) {
        audioErrorNumbers.add(qn);
        hasAudioError = true;
      }
    }
    if (q.type === 'audioMultipleChoice' && q.groupKey) {
      let isFirstInGroup =
        questions.findIndex((quest) => quest.type === 'audioMultipleChoice' && quest.groupKey === q.groupKey) === i;
      if (audioMultipleChoicePartialIsGroupFirst !== undefined) {
        isFirstInGroup = audioMultipleChoicePartialIsGroupFirst;
      }
      if (isFirstInGroup && !q.audio) {
        audioErrorNumbers.add(qn);
        hasAudioError = true;
      }
    }
  });

  const summaryParts = [];
  if (hasQuestionError) {
    summaryParts.push(`Add question text (questions ${joinQuestionNums(questionErrorNumbers)}).`);
  }
  if (hasInstructionError) {
    summaryParts.push(`Add instruction text (questions ${joinQuestionNums(instructionErrorNumbers)}).`);
  }
  if (hasAudioError) {
    summaryParts.push(`Add an audio file (questions ${joinQuestionNums(audioErrorNumbers)}).`);
  }
  if (hasOptionError) {
    summaryParts.push(`Fill in every option (questions ${joinQuestionNums(optionErrorNumbers)}).`);
  }

  if (summaryParts.length) {
    const allIndices = [...questionErrorNumbers, ...optionErrorNumbers, ...audioErrorNumbers].map((n) => n - 1);
    error.push({
      type: 'question_error',
      questionIndices: [...new Set(allIndices)],
      message: summaryParts.join('\n'),
    });
  }

  return error;
}

export function formatAssignmentValidationMessages(errors) {
  return errors.map((e) => e.message).filter(Boolean);
}
